import { computed, ref, watch, onMounted, type Ref } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import {
	MAX_PRELOADED_ITEMS,
	pageQuery
} from "~/utils/categories/resolveListingPage";

/**
 * Result shape of a product page fetch (subset of listProductByFilters result).
 */
export interface CategoryPageResult {
	Items?: unknown[];
	ItemCount?: number;
}

export interface UseCategoryPaginationOptions {
	/** Page the SSR render started on (from `?p`). */
	initialPageNo?: number;
	/** SSR-rendered items for the initial page. */
	initialItems?: FilteredProductItem[];
	/** Total item count from the initial fetch. */
	initialItemCount?: number;
	/** Current page size as a string (cookie/Storyblok-backed select value). */
	pageSize: Ref<string>;
	/**
	 * Fetches a single page of products with the component's current
	 * filter/sort/population context. Owned by the component — the
	 * composable only owns paging.
	 */
	fetchPage: (page: number) => Promise<CategoryPageResult | undefined>;
	/**
	 * `loadMore`: the list holds pages 1..currentPage, so a `?p` change
	 * (Back/forward) rebuilds that range instead of showing one page.
	 */
	mode?: "numbered" | "loadMore";
}

/**
 * Pagination for category listings: numbered (NF-280), or "show more" via
 * `loadMore()` when the template's `productListing.pagination` is `loadMore`.
 *
 * Owns the current page, its items, and the `?p` URL sync. Page changes
 * arrive through the URL (pagination links / back-forward) and are handled
 * by the `?p` watcher. Filter/sort concerns stay in the consuming
 * component; on filter/sort changes call `resetToFirstPage()` +
 * `applyResults(data)`.
 */
export function useCategoryPagination(options: UseCategoryPaginationOptions) {
	const {
		initialPageNo = 1,
		initialItems = [],
		initialItemCount = 0,
		pageSize,
		fetchPage,
		mode = "numbered"
	} = options;

	const route = useRoute();

	const currentPage = ref(initialPageNo);
	const currentItems = ref<FilteredProductItem[]>(initialItems);
	const itemCount = ref<number>(initialItemCount);
	const isLoaded = ref<boolean>(true);

	const maxPage = () => {
		// An empty/zero page size means "fetch all":
		// everything is on one page, so paging is disabled.
		const pageSizeNum = parseInt(pageSize.value, 10);
		if (!pageSizeNum || pageSizeNum <= 0) return 1;
		return Math.max(1, Math.ceil(itemCount.value / pageSizeNum));
	};

	// Navigates only when the query actually changed, to avoid redundant
	// history entries.
	const syncUrl = (
		queryParams: Record<string, string>,
		{ replace = false }: { replace?: boolean } = {}
	) => {
		const currentQueryKeys = Object.keys(route.query);
		const newQueryKeys = Object.keys(queryParams);
		const queryChanged =
			currentQueryKeys.length !== newQueryKeys.length ||
			newQueryKeys.some((k) => route.query[k] !== queryParams[k]);
		if (!queryChanged) return;
		if (replace) navigateTo({ query: queryParams }, { replace });
		else navigateTo({ query: queryParams });
	};

	// Updates only the `p` param in the URL, preserving filter/sort.
	const syncPageInUrl = (urlPage: number, options?: { replace?: boolean }) =>
		syncUrl(pageQuery(route.query, urlPage), options);

	// Bumped by every list change; a fetch that resolves after a newer one
	// started (e.g. "show more" in flight when the sort changes) is dropped.
	let requestId = 0;

	const applyResults = (data: CategoryPageResult | undefined) => {
		requestId++;
		currentItems.value = (data?.Items as FilteredProductItem[]) ?? [];
		itemCount.value = data?.ItemCount ?? 0;
	};

	// Filter/sort changes always collapse back to page 1.
	const resetToFirstPage = () => {
		requestId++;
		currentPage.value = 1;
	};

	/**
	 * Fetches `page` and hands its items to `merge` unless a newer request
	 * started meanwhile. Returns whether the page was applied.
	 */
	const runFetch = async (
		page: number,
		merge: (incoming: FilteredProductItem[]) => FilteredProductItem[],
		load: () => Promise<CategoryPageResult | undefined> = () => fetchPage(page)
	): Promise<boolean> => {
		const id = ++requestId;
		isLoaded.value = false;
		try {
			const data = await load();
			if (id !== requestId) return false;

			currentItems.value = merge((data?.Items as FilteredProductItem[]) ?? []);
			if (typeof data?.ItemCount === "number") itemCount.value = data.ItemCount;
			currentPage.value = page;
			return true;
		} catch (error) {
			console.error(`[category] could not load page ${page}`, error);
			return false;
		} finally {
			// A superseded request leaves the loading state to the newer one.
			if (id === requestId) isLoaded.value = true;
		}
	};

	const loadPage = async (n: number) => {
		const target = Math.min(Math.max(1, Math.floor(n) || 1), maxPage());
		const id = requestId + 1;
		const applied = await runFetch(target, (incoming) => incoming);

		if (applied) {
			if (target !== n) syncPageInUrl(target);
		} else if (id === requestId) {
			// Failed, not superseded: point `?p` back at the page on screen so
			// the same link can be clicked again.
			syncPageInUrl(currentPage.value, { replace: true });
		}
	};

	const hasMore = computed(() => currentPage.value < maxPage());

	// Offset pages can repeat a product when the order shifts between
	// requests (ties, stock, catalog edits); a repeat would duplicate a key.
	const productKey = (item: FilteredProductItem) =>
		`${item.Id}:${item.PartNo ?? ""}`;

	const appendUnique = (
		list: FilteredProductItem[],
		incoming: FilteredProductItem[]
	) => {
		const seen = new Set(list.map(productKey));
		const added = incoming.filter((item) => {
			const key = productKey(item);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
		return [...list, ...added];
	};

	// Same cap the listing pages use when they render a `?p` URL.
	const maxPreloadedPages = () =>
		Math.max(
			1,
			Math.floor(MAX_PRELOADED_ITEMS / (parseInt(pageSize.value, 10) || 1))
		);

	/** Load-more mode: show pages 1..n, as the listing page renders `?p=n`. */
	const restorePages = async (n: number) => {
		const last = Math.min(Math.max(1, n), maxPage(), maxPreloadedPages());
		const pages = Array.from({ length: last }, (_, index) => index + 1);
		const applied = await runFetch(
			last,
			(incoming) => appendUnique([], incoming),
			async () => {
				const results = await Promise.all(pages.map(fetchPage));
				return {
					Items: results.flatMap((result) => result?.Items ?? []),
					ItemCount: results.at(-1)?.ItemCount
				};
			}
		);
		if (applied && last !== n) syncPageInUrl(last, { replace: true });
	};

	/**
	 * "Show more" mode: appends the next page and moves `?p` with it (replace,
	 * no history entry), so a refresh or Back lands on the same list — the
	 * listing pages load pages 1..p for `?p` in this mode. `currentPage` is
	 * already the new page when `?p` changes, so the watcher below ignores it.
	 */
	const loadMore = async () => {
		if (!hasMore.value) return;

		const next = currentPage.value + 1;
		const applied = await runFetch(next, (incoming) =>
			appendUnique(currentItems.value, incoming)
		);
		if (applied) syncPageInUrl(next, { replace: true });
	};

	// Pagination links, back/forward navigation and external `?p` changes.
	watch(
		() => route.query.p,
		(newP) => {
			const n = Math.max(1, parseInt(newP as string, 10) || 1);
			if (n === currentPage.value) return;
			if (mode === "loadMore") restorePages(n);
			else loadPage(n);
		}
	);

	// If the SSR `?p` overshot the category (stale link → empty page), clamp
	// to the last real page. Only an EMPTY initial page is treated as proof
	// of overshoot — itemCount alone can be transiently wrong during
	// hydration and must not be allowed to rewrite a valid deep link.
	onMounted(() => {
		if (currentItems.value.length === 0 && currentPage.value > maxPage()) {
			loadPage(currentPage.value);
			return;
		}
		// Load-more lists are capped (`?p=20` may render 8 pages): make the URL
		// say what is on screen. The watcher then sees the current page.
		if (mode === "loadMore")
			syncPageInUrl(currentPage.value, { replace: true });
	});

	return {
		currentPage,
		currentItems,
		itemCount,
		isLoaded,
		hasMore,
		loadMore,
		resetToFirstPage,
		applyResults,
		syncUrl
	};
}
